import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Popper from '@mui/material/Popper';
import { useTranslation } from 'react-i18next';
import { fetchLanguages } from '../i18n';
import { CURRENCIES, CURRENCY_SELECTION_ENABLED, useCurrency } from '../utils/currency';
import brandRing from './Assets/he-chrome/brand-ring.svg';
import brandMark from './Assets/he-chrome/brand-mark.svg';
import './TopBar.css';

// Site top bar, laid out like the header in src/Components/He-Website-Source:
// links on the left, the logo centred, membership and account actions on the
// right. Phones get a menu button, the logo and a My Space button.
function TopBar({
  cartCount = 0,
  isLoggedIn = false,
  onCartClick,
  onMySpaceClick,
  onSignInClick,
  coins,
  onLogout,
  overlay = false,
}) {
  const MOBILE_BREAKPOINT = 960;

  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= MOBILE_BREAKPOINT : false
  );
  const barRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const [activeMenu, setActiveMenu] = useState(null); // 'lifeinform' | 'experts' | 'membership' | 'language' | 'myspace'
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const timerRef = useRef(null);
  const { currency, setCurrency } = useCurrency();

  const normalizeLocale = (code) => {
    const c = (code || '').split(/[-_]/)[0].toLowerCase();
    if (c.startsWith('zh')) return 'cn';
    return c || 'en';
  };

  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    const fromI18n = normalizeLocale(i18n.language);
    const fromStorage = localStorage.getItem('language') || 'en';
    return fromI18n || fromStorage;
  });
  const [availableLanguages, setAvailableLanguages] = useState([]);

  // Clear timeouts on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Fetch available languages from API
  useEffect(() => {
    const fallbackLanguages = [
      { Id: '2224', LookupDetail: 'en', LookupDesc: 'English' },
      { Id: '2225', LookupDetail: 'id', LookupDesc: 'Bahasa Indonesia' },
      { Id: '2226', LookupDetail: 'cn', LookupDesc: '中文' },
    ];

    fetchLanguages().then(languages => {
      if (!languages || languages.length === 0) {
        setAvailableLanguages(fallbackLanguages);
        return;
      }

      const mergedLangs = [...languages];
      const existingCodes = mergedLangs.map(lang => {
        let code = (lang.LookupDetail || '').match(/^(\S+)/)?.[1]?.toLowerCase() || '';
        if (code.startsWith('zh')) code = 'cn';
        return code;
      });

      fallbackLanguages.forEach(fallback => {
        let fallbackCode = (fallback.LookupDetail || '').match(/^(\S+)/)?.[1]?.toLowerCase() || '';
        if (fallbackCode.startsWith('zh')) fallbackCode = 'cn';
        if (!existingCodes.includes(fallbackCode)) {
          mergedLangs.push(fallback);
        }
      });

      setAvailableLanguages(mergedLangs);
    }).catch(() => {
      setAvailableLanguages(fallbackLanguages);
    });
  }, []);

  // Reports whether the server accepted the change. Callers must await this before reloading:
  // `syncProfileLanguageFromGetProfile` re-reads the profile on load and overwrites the local
  // language keys, so a reload that beats this POST silently reverts the user's choice.
  const updateUserLanguage = async (langId, langDesc) => {
    try {
      const userId = localStorage.getItem('userID');
      if (!userId) return true; // Signed out — nothing to persist server-side.
      const response = await fetch('https://xianhefairy.com/LotusFairy_API/api/User/LanguageUpdate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ID: parseInt(userId), LANG_ID: langId, LANG_DESC: langDesc })
      });
      if (!response.ok) console.error('Language update rejected:', response.status);
      return response.ok;
    } catch (error) {
      console.error('Error updating user language:', error);
      return false;
    }
  };

  // Desktop dropdowns open on hover and linger briefly so the pointer can reach them
  const handleOpen = (e, name) => {
    if (isMobile) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    setMenuAnchorEl(e.currentTarget);
    setActiveMenu(name);
  };

  const handleClose = () => {
    if (isMobile) return;
    timerRef.current = setTimeout(() => {
      setActiveMenu(null);
      setMenuAnchorEl(null);
    }, 400);
  };

  const keepOpen = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  };

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth <= MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      if (!mobile) setOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Close the phone menu on outside click or Escape
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (barRef.current && !barRef.current.contains(e.target)) setOpen(false);
    };
    const handleKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  // Close the phone menu whenever the route changes
  useEffect(() => {
    setOpen(false);
    setActiveMenu(null);
  }, [location.pathname]);

  const isActive = (path) => {
    if (path === '/natal') return ['/main', '/natal', '/DigitalCharm', '/chart'].includes(location.pathname);
    if (path === '/schedule') return ['/schedule', '/myappointment', '/balanceinspace'].includes(location.pathname);
    if (path === '/pricing') return ['/pricing', '/affiliatepartner'].includes(location.pathname);
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const go = (path) => {
    navigate(path);
    setActiveMenu(null);
    setOpen(false);
  };

  const handleCartClick = () => {
    setOpen(false);
    if (!isLoggedIn) { handleSignIn(); return; }
    if (onCartClick) onCartClick(); else navigate('/cart');
  };
  const handleMySpaceClick = () => { if (onMySpaceClick) onMySpaceClick(); else navigate('/account'); };
  const handleSignIn = () => { if (onSignInClick) onSignInClick(); };

  // Signed-in users change language from Account > Language Settings, which persists the choice
  // to their profile. The switcher stays for signed-out visitors, who have no profile to save to.
  const canChangeLang = !isLoggedIn;

  // Short language label, e.g. "Eng"
  const getLangLabel = () => {
    const match = availableLanguages.find(l => {
      let code = (l.LookupDetail || '').match(/^(\S+)/)?.[1]?.toLowerCase() || '';
      if (code.startsWith('zh')) code = 'cn';
      return code === selectedLanguage;
    });
    const raw = match ? match.LookupDetail.split(' ')[0].substring(0, 3) : selectedLanguage;
    return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
  };

  const selectLanguage = async (lang, langCode) => {
    setSelectedLanguage(langCode);
    localStorage.setItem('language', langCode);
    localStorage.setItem('languageId', lang.Id);
    // LanguageSettings seeds its ticked row from `profileLanguage`, so skipping it here
    // leaves that page showing the previous language while the app renders the new one.
    localStorage.setItem('profileLanguage', langCode);
    sessionStorage.setItem('topBarLangOverride', langCode);
    await i18n.changeLanguage(langCode);
    setActiveMenu(null);
    setOpen(false);
    // Await the save before reloading — a fire-and-forget call can lose the race
    // with the profile sync, which then restores the previous language.
    await updateUserLanguage(lang.Id, lang.LookupDesc);
    window.location.reload();
  };

  const renderLangItems = () =>
    availableLanguages.map((lang) => {
      let langCode = (lang.LookupDetail || '').match(/^(\S+)/)?.[1]?.toLowerCase() || '';
      if (langCode.startsWith('zh')) langCode = 'cn';
      const isSelected = selectedLanguage === langCode;
      return (
        <button
          type="button"
          key={lang.Id}
          className={`he-topbar-menu-item${isSelected ? ' is-selected' : ''}`}
          onClick={() => selectLanguage(lang, langCode)}
        >
          {isSelected && '✓ '}{lang.LookupDesc}
        </button>
      );
    });

  const renderCurrencyItems = () =>
    Object.values(CURRENCIES).map((c) => (
      <button
        type="button"
        key={c.code}
        className={`he-topbar-menu-item${currency === c.code ? ' is-selected' : ''}`}
        onClick={() => { setCurrency(c.code); setActiveMenu(null); }}
      >
        {currency === c.code && '✓ '}{c.label}
      </button>
    ));

  // Links grouped as in the design; the extra destinations live in each group's menu
  const menus = {
    lifeinform: [
      { label: t('mainPage.landing'), path: '/main' },
      { label: t('natalChart.yourLifeChart'), path: '/natal' },
      { label: t('mainPage.myAmulet'), path: '/DigitalCharm' },
    ],
    experts: [
      { label: t('mainPage.makeAppointment'), path: '/schedule' },
      { label: t('mainPage.checkAppointment'), path: '/myappointment' },
      { label: t('mainPage.balanceInSpace'), path: '/balanceinspace' },
    ],
    membership: [
      { label: t('mainPage.plan'), path: '/pricing' },
      { label: t('mainPage.affiliatePartner'), path: '/affiliatepartner' },
    ],
  };

  const navItems = [
    { key: 'lifeinform', label: t('topBar.lifeInForm', 'Life in form'), path: '/natal', menu: menus.lifeinform },
    { key: 'studio', label: t('topBar.studio', 'Studio'), path: '/courses' },
    { key: 'experts', label: t('topBar.theExperts', 'The Experts'), path: '/schedule', menu: menus.experts },
    { key: 'collection', label: t('topBar.collection', 'Collection'), path: '/shop' },
  ];
  const membershipItem = { key: 'membership', label: t('topBar.membership', 'Membership'), path: '/pricing', menu: menus.membership };

  const renderDesktopLink = (item) => {
    const className = `he-topbar-link${isActive(item.path) ? ' is-active' : ''}`;
    if (!item.menu) {
      return (
        <Link key={item.key} to={item.path} className={className}>{item.label}</Link>
      );
    }
    return (
      <span key={item.key} onMouseEnter={(e) => handleOpen(e, item.key)} onMouseLeave={handleClose}>
        <button
          type="button"
          className={className}
          aria-haspopup="menu"
          aria-expanded={activeMenu === item.key}
          onClick={() => go(item.path)}
          onFocus={(e) => handleOpen(e, item.key)}
        >
          {item.label}
        </button>
        <Popper open={activeMenu === item.key} anchorEl={menuAnchorEl} placement="bottom" style={{ zIndex: 23500 }}>
          <div onMouseEnter={keepOpen} onMouseLeave={handleClose} style={{ paddingTop: 12 }}>
            <div className="he-topbar-menu-box" role="menu">
              {item.menu.map((entry) => (
                <button type="button" role="menuitem" key={entry.path} className="he-topbar-menu-item" onClick={() => go(entry.path)}>
                  {entry.label}
                </button>
              ))}
            </div>
          </div>
        </Popper>
      </span>
    );
  };

  const renderPanelLink = (item) => {
    const className = `he-topbar-link${isActive(item.path) ? ' is-active' : ''}`;
    if (!item.menu) {
      return (
        <button type="button" key={item.key} className={className} onClick={() => go(item.path)}>
          {item.label}
        </button>
      );
    }
    const expanded = activeMenu === item.key;
    return (
      <div key={item.key}>
        <button
          type="button"
          className={className}
          aria-expanded={expanded}
          onClick={() => setActiveMenu(expanded ? null : item.key)}
        >
          {item.label}
          <span aria-hidden="true" style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}>⌄</span>
        </button>
        {expanded && (
          <div className="he-topbar-sub">
            {item.menu.map((entry) => (
              <button type="button" key={entry.path} onClick={() => go(entry.path)}>{entry.label}</button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const cartLabel = (
    <>
      {t('topBar.cart', 'Cart')}
      {isLoggedIn && cartCount > 0 && (
        <span className="he-topbar-count">{cartCount > 99 ? '99+' : cartCount}</span>
      )}
    </>
  );

  const brand = (
    <Link to="/main" className="he-topbar-brand" aria-label="Hé home">
      <img src={brandRing} width="70" height="72" alt="" />
      <img className="he-topbar-brand-inner" src={brandMark} alt="" />
    </Link>
  );

  const barClass = `he-topbar${overlay ? ' is-overlay' : ''}${open ? ' is-open' : ''}`;

  if (isMobile) {
    return (
      <div className={barClass} ref={barRef}>
        <button
          type="button"
          className="he-topbar-burger"
          aria-label={open ? 'Close navigation' : 'Open navigation'}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          {open ? '×' : '☰'}
        </button>

        {brand}

        <button
          type="button"
          className="he-topbar-space"
          aria-label={isLoggedIn ? t('tryoutpage.myspace') : 'Sign In'}
          data-myspace-trigger="true"
          onClick={() => {
            setOpen(false);
            if (isLoggedIn) handleMySpaceClick(); else handleSignIn();
          }}
        >
          ♙
          {isLoggedIn && cartCount > 0 && (
            <span className="he-topbar-badge">{cartCount > 99 ? '99+' : cartCount}</span>
          )}
        </button>

        {open && (
          <nav className="he-topbar-panel" aria-label="Site">
            {navItems.map(renderPanelLink)}
            {renderPanelLink(membershipItem)}

            <div className="he-topbar-panel-actions">
              {isLoggedIn && (
                <div className="he-topbar-gems">{coins ?? 0} {t('tryoutpage.gems')}</div>
              )}
              <button type="button" className="he-topbar-small" onClick={handleCartClick}>{cartLabel}</button>
              {isLoggedIn ? (
                <>
                  <button type="button" className="he-topbar-small" onClick={() => go('/account')}>{t('tryoutpage.account')}</button>
                  <button type="button" className="he-topbar-small" onClick={() => go('/purchasehistory')}>{t('tryoutpage.history')}</button>
                  {onLogout && (
                    <button type="button" className="he-topbar-small" onClick={() => { setOpen(false); onLogout(); }}>{t('tryoutpage.logout')}</button>
                  )}
                </>
              ) : (
                <button type="button" className="he-topbar-small" onClick={() => { setOpen(false); handleSignIn(); }}>Sign In</button>
              )}
              {canChangeLang && <div className="he-topbar-choices">{renderLangItems()}</div>}
              {CURRENCY_SELECTION_ENABLED && <div className="he-topbar-choices">{renderCurrencyItems()}</div>}
            </div>
          </nav>
        )}
      </div>
    );
  }

  return (
    <div className={barClass} ref={barRef}>
      <nav className="he-topbar-nav" aria-label="Site">
        {navItems.map(renderDesktopLink)}
      </nav>

      {brand}

      <div className="he-topbar-right">
        {renderDesktopLink(membershipItem)}

        <button type="button" className="he-topbar-small" onClick={handleCartClick}>{cartLabel}</button>

        {isLoggedIn ? (
          <button type="button" className="he-topbar-small" data-myspace-trigger="true" onClick={handleMySpaceClick}>
            {t('tryoutpage.myspace')}
          </button>
        ) : (
          <button type="button" className="he-topbar-small" onClick={handleSignIn}>Sign In</button>
        )}

        {CURRENCY_SELECTION_ENABLED && (
          <span onMouseEnter={(e) => handleOpen(e, 'currency')} onMouseLeave={handleClose}>
            <button type="button" className="he-topbar-small" aria-haspopup="menu">{currency}⌄</button>
            <Popper open={activeMenu === 'currency'} anchorEl={menuAnchorEl} placement="bottom-end" style={{ zIndex: 23500 }}>
              <div onMouseEnter={keepOpen} onMouseLeave={handleClose} style={{ paddingTop: 8 }}>
                <div className="he-topbar-menu-box">{renderCurrencyItems()}</div>
              </div>
            </Popper>
          </span>
        )}

        {/* Signed-in users change language from Account > Language Settings */}
        {canChangeLang && (
          <span onMouseEnter={(e) => handleOpen(e, 'language')} onMouseLeave={handleClose}>
            <button
              type="button"
              className="he-topbar-small"
              aria-haspopup="menu"
              onFocus={(e) => handleOpen(e, 'language')}
            >
              {getLangLabel()}⌄
            </button>
            <Popper open={activeMenu === 'language'} anchorEl={menuAnchorEl} placement="bottom-end" style={{ zIndex: 23500 }}>
              <div onMouseEnter={keepOpen} onMouseLeave={handleClose} style={{ paddingTop: 8 }}>
                <div className="he-topbar-menu-box">{renderLangItems()}</div>
              </div>
            </Popper>
          </span>
        )}
      </div>
    </div>
  );
}

export default TopBar;
